'use client';

import './carousel.scss';

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import Image from 'next/image';
import {
  ButtonHTMLAttributes,
  HTMLAttributes,
  KeyboardEvent,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { MediaProps } from '@sola/schema/lib/schema.js';
import { formatDuration } from '@sola/utils/video.js';

import { Video } from '../../atoms/video/video';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselLazyLoadImageProps = {
  media: MediaProps | undefined;
  index?: number;
  isVideoThumbnail?: boolean;
  className?: string;
};

type CarouselLazyLoadVideoProps = {
  media: MediaProps;
  index?: number;
  className?: string;
  autoplay?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  slidesLoaded: number[];
  selectedSlide: number;
} & CarouselProps;

type CarouselItemContextProps = {
  isHidden: boolean;
};

const CarouselContext = createContext<CarouselContextProps | null>(null);

const CarouselItemContext = createContext<CarouselItemContextProps | null>(
  null,
);

const useCarousel = () => {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
};

const useCarouselItem = () => {
  const context = useContext(CarouselItemContext);

  if (!context) {
    throw new Error('useCarouselItem must be used within a <CarouselItem />');
  }

  return context;
};

const Carousel = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [slidesLoaded, setSlidesLoaded] = useState<number[]>([]);
    const [selectedSlide, setSelectedSlide] = useState(0);

    const onSelect = useCallback(() => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      setSelectedSlide(api.selectedScrollSnap());
    }, [api]);

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    const updateSlidesLoaded = useCallback(() => {
      if (!api) {
        return;
      }
      setSlidesLoaded((prevSlidesLoaded) => {
        if (prevSlidesLoaded.length === api.slideNodes().length) {
          api.off('slidesInView', updateSlidesLoaded);
          return prevSlidesLoaded;
        }
        const inView = api
          .slidesInView()
          .filter((index) => !prevSlidesLoaded.includes(index));
        if (inView.length === 0) {
          return prevSlidesLoaded;
        }
        // @TODO - possibly look into loading before and after slide too
        return prevSlidesLoaded.concat(inView);
      });
    }, [api]);

    const setFocusTrap = useCallback(() => {
      if (!api) {
        return;
      }
      const slidesInView = api?.slidesInView() ?? [];
      let slidesVisible;
      if (slidesInView.length > 1) {
        if (canScrollNext) {
          slidesVisible = slidesInView.slice(0, -1);
        } else {
          slidesVisible = slidesInView.slice(1);
        }
      } else {
        slidesVisible = [api?.selectedScrollSnap()];
      }

      api?.slideNodes().forEach((slide, index) => {
        const isVisible = slidesVisible.includes(index);
        const interactiveElements = slide.querySelectorAll('a, button, iframe');
        interactiveElements.forEach((element) => {
          if (isVisible) {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    }, [api, canScrollNext]);

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }
      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }
      onSelect();
      api.on('reInit', onSelect);
      api.on('select', onSelect);
      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    useEffect(() => {
      if (!api) {
        return;
      }
      updateSlidesLoaded();
      api.on('reInit', updateSlidesLoaded);
      api.on('slidesInView', updateSlidesLoaded);
      return () => {
        api?.off('slidesInView', updateSlidesLoaded);
      };
    }, [api, updateSlidesLoaded]);

    useEffect(() => {
      if (!api) {
        return;
      }
      setFocusTrap();
      api.on('reInit', setFocusTrap);
      api.on('slidesInView', setFocusTrap);
      return () => {
        api?.off('slidesInView', setFocusTrap);
      };
    }, [api, setFocusTrap]);

    return (
      <CarouselContext.Provider
        value={useMemo(
          () => ({
            carouselRef,
            api,
            opts,
            orientation:
              orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext,
            slidesLoaded,
            selectedSlide,
          }),
          [
            carouselRef,
            api,
            opts,
            orientation,
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext,
            slidesLoaded,
            selectedSlide,
          ],
        )}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="Carousel">
      <div
        ref={ref}
        className={cn(
          'Carousel__container flex',
          orientation === 'horizontal' ? 'flex-row' : 'flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    index: number;
  }
>(({ className, children, ...props }, ref) => {
  const { api } = useCarousel();
  const isHidden = !api?.slidesInView()?.includes(props.index as number);

  return (
    <CarouselItemContext.Provider
      value={useMemo(() => ({ isHidden }), [isHidden])}
    >
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        aria-hidden={isHidden}
        className={cn(
          'Carousel__slide',
          className ?? 'Carousel__slide--default',
        )}
        {...props}
      >
        {children}
      </div>
    </CarouselItemContext.Provider>
  );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselLazyLoadImage = ({
  index = 0,
  media,
  isVideoThumbnail = false,
  className,
}: CarouselLazyLoadImageProps) => {
  const { isHidden } = useCarouselItem();
  const { slidesLoaded } = useCarousel();
  const [hasLoaded, setHasLoaded] = useState(!isHidden);

  useEffect(() => {
    if (slidesLoaded.indexOf(index) > -1) {
      setHasLoaded(true);
    }
  }, [slidesLoaded, index]);

  const handleImageLoad = () => {
    setHasLoaded(true);
  };

  if (!media || !media?.image) {
    return (
      <div
        className="w-full h-full"
        style={{
          backgroundImage: `url(/placeholder.svg)`,
          backgroundSize: '20px',
        }}
      />
    );
  }

  return (
    <div
      className={cn(
        'Carousel__media Carousel__media--lazy-load',
        hasLoaded ? 'has-loaded' : '',
        className,
      )}
    >
      {isVideoThumbnail && media.video && (
        <div className="Carousel__video-thumb">
          <div className="Carousel__video-thumb__overlay" />
          <div className="Carousel__video-thumb__info">
            {media.video.duration && (
              <div className="label-xl label-potent p-comfy-lg">
                {formatDuration(media.video.duration)}
              </div>
            )}
          </div>
          <div className="Carousel__video-thumb__play">
            <ArkIcon iconName="play" fill="currentColor" />
          </div>
        </div>
      )}
      {!hasLoaded && <span className="Carousel__media__spinner" />}
      {media.image && hasLoaded && (
        <Image
          className="Carousel__img Carousel__img--lazy"
          onLoad={handleImageLoad}
          src={hasLoaded ? media.image.url : ''}
          alt={media.image.alt || ''}
          data-src={media.image.url}
          fill
          style={{
            objectPosition: media.image.focus
              ? `${media.image.focus.x} ${media.image.focus.y}`
              : undefined,
          }}
        />
      )}
    </div>
  );
};

const CarouselLazyLoadVideo = ({
  index = 0,
  media,
  className,
  autoplay = false,
}: CarouselLazyLoadVideoProps) => {
  const [allowPlaying, setAllowPlaying] = useState(false);
  const { selectedSlide, slidesLoaded } = useCarousel();
  const [hasLoaded, setHasLoaded] = useState(slidesLoaded.indexOf(index) > -1);

  useEffect(() => {
    if (selectedSlide === index) {
      setAllowPlaying(true);
    } else {
      setAllowPlaying(false);
    }
  }, [selectedSlide, index]);

  useEffect(() => {
    if (slidesLoaded.indexOf(index) > -1) {
      setHasLoaded(true);
    }
  }, [slidesLoaded, index]);

  if (!media.video) {
    return;
  }
  return (
    <div className="CarouselVideo CarouselVideo--lazy flex">
      <div className={className}>
        {hasLoaded && (
          <Video
            {...media.video}
            poster={media.image}
            allowPlaying={allowPlaying}
            autoplay={autoplay}
          />
        )}
      </div>
    </div>
  );
};

const CarouselPrevious = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation(['core']);
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      className={cn('Carousel__arrow', className)}
      {...props}
    >
      <ArkIcon iconName="chevron-left" size="20" />
      <span className="sr-only"> {t('core:gallery.prev')}</span>
    </button>
  );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation();
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      disabled={!canScrollNext}
      onClick={scrollNext}
      className={cn('Carousel__arrow', className)}
      {...props}
    >
      <ArkIcon iconName="chevron-right" size="20" />
      <span className="sr-only"> {t('core:gallery.next')}</span>
    </button>
  );
});
CarouselNext.displayName = 'CarouselNext';

const CarouselProgress = () => {
  const { scrollNext, selectedSlide, api } = useCarousel();

  const progressText = useMemo(() => {
    return `${selectedSlide + 1} / ${api?.slideNodes().length}`;
  }, [selectedSlide, api]);

  return (
    <button
      type="button"
      className="Carousel__count-display"
      onClick={() => scrollNext()}
    >
      {progressText}
    </button>
  );
};

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselLazyLoadVideo,
  CarouselPrevious,
  CarouselNext,
  CarouselProgress,
};
